import config from '../config';

/**
 * Validate an email address format
 * @param {string} email - The email address to validate
 * @returns {boolean} True if the email is valid, false otherwise
 */
export const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Send a document for signing using Resend via Netlify Function
 * @param {Object} params - The parameters for sending the document
 * @param {string} params.documentId - The document ID
 * @param {string} params.recipientEmail - The recipient's email address
 * @param {string} params.subject - The email subject
 * @param {string} params.message - The email message
 * @param {string} [params.senderName] - The sender's name
 * @param {string} [params.senderEmail] - The sender's email
 * @param {string} [params.fileData] - The document file data (base64 or storage URL)
 * @param {string} [params.fileName] - The document file name
 * @returns {Promise<Object>} A promise that resolves to the send result
 */
export const sendDocumentForSigning = async ({
  documentId,
  recipientEmail,
  subject,
  message,
  senderName,
  senderEmail,
  fileData,
  fileName
}) => {
  try {
    // Generate a signing link with recipient email as a query parameter
    const signingLink = `${window.location.origin}/sign/${documentId}`;
    
    // For development/testing, log the email details
    console.log('Sending document for signing:');
    console.log('- Document ID:', documentId);
    console.log('- Recipient:', recipientEmail);
    console.log('- Subject:', subject);
    console.log('- Signing Link:', signingLink);
    console.log('- File data provided:', !!fileData);
    
    try {
      // Send email using Netlify Function
      const response = await fetch('/.netlify/functions/send-document', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          documentId,
          recipientEmail,
          subject,
          message,
          signingLink,
          senderName,
          senderEmail,
          fileData,
          fileName
        }),
      });
      
      console.log('Function response status:', response.status);
      
      // Try to parse the response body
      let responseData;
      try {
        responseData = await response.json();
        console.log('Function response data:', responseData);
      } catch (parseError) {
        console.error('Error parsing response:', parseError);
        responseData = { error: 'Could not parse function response' };
      }
      
      // Check if the response was successful
      if (!response.ok) {
        const errorMessage = responseData.error || response.statusText || 'Unknown error';
        console.warn(`Warning: Function returned error (${response.status}): ${errorMessage}`);
        
        // Return a partial success response since the email might still have been sent
        return {
          success: false,
          documentId,
          recipientEmail,
          signingLink,
          error: errorMessage,
          sentAt: new Date().toISOString()
        };
      }
      
      // Return the successful response
      return {
        success: true,
        ...responseData,
        signingLink,
        sentAt: new Date().toISOString()
      };
    } catch (networkError) {
      console.error('Network error when sending email:', networkError);
      // Return a partial success response since the email might still have been sent
      return {
        success: false,
        documentId,
        recipientEmail,
        signingLink,
        error: networkError.message || 'Network error',
        sentAt: new Date().toISOString()
      };
    }
    
  } catch (error) {
    console.error('Error sending document for signing:', error);
    throw error;
  }
};

/**
 * Send a signed document notification using Resend via Netlify Function
 * @param {Object} params - The parameters for sending the notification
 * @param {string} params.documentId - The document ID
 * @param {string} params.recipientEmail - The recipient's email address
 * @param {string} params.signerEmail - The signer's email address
 * @param {string} params.viewLink - The link to view the signed document
 * @param {string} [params.fileData] - The document file data (base64 or storage URL)
 * @param {string} [params.fileName] - The document file name
 * @param {boolean} [params.isFinalSignature] - Whether this is the final signature for the document
 * @returns {Promise<Object>} A promise that resolves to the send result
 */
export const sendSignedDocumentNotification = async ({
  documentId,
  recipientEmail,
  signerEmail,
  viewLink,
  fileData,
  fileName,
  isFinalSignature = true
}) => {
  try {
    // Validate required parameters
    if (!documentId) {
      throw new Error('Document ID is required');
    }
    if (!recipientEmail) {
      throw new Error('Recipient email is required');
    }
    if (!signerEmail) {
      throw new Error('Signer email is required');
    }
    
    // Use provided viewLink or generate one if not provided
    const documentViewLink = viewLink || `${window.location.origin}/complete/${documentId}`;
    
    // For development/testing, log the email details
    console.log('Sending signed document notification:');
    console.log('- Document ID:', documentId);
    console.log('- Recipient:', recipientEmail);
    console.log('- Signer:', signerEmail);
    console.log('- View Link:', documentViewLink);
    
    try {
      // Send notification using Netlify Function
      const response = await fetch('/.netlify/functions/send-notification', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          documentId,
          recipientEmail,
          signerEmail,
          viewLink: documentViewLink,
          fileData,
          fileName: fileName || 'signed_document.pdf',
          isFinalSignature
        }),
      });
      
      console.log('Function notification response status:', response.status);
      
      // Try to parse the response body
      let responseData;
      try {
        responseData = await response.json();
        console.log('Function notification response data:', responseData);
      } catch (parseError) {
        console.error('Error parsing response:', parseError);
        responseData = { error: 'Could not parse function response' };
      }
      
      // Check if the response was successful
      if (!response.ok) {
        const errorMessage = responseData.error || response.statusText || 'Unknown error';
        console.warn(`Warning: Function returned error (${response.status}): ${errorMessage}`);
        
        // Return a partial success response since the email might still have been sent
        return {
          success: false,
          documentId,
          recipientEmail,
          signerEmail,
          viewLink: documentViewLink,
          error: errorMessage,
          sentAt: new Date().toISOString()
        };
      }
      
      // Return the successful response
      return {
        success: true,
        ...responseData,
        viewLink: documentViewLink,
        sentAt: new Date().toISOString()
      };
    } catch (networkError) {
      console.error('Network error when sending notification:', networkError);
      // Return a partial success response since the email might still have been sent
      return {
        success: false,
        documentId,
        recipientEmail,
        signerEmail,
        viewLink: documentViewLink,
        error: networkError.message || 'Network error',
        sentAt: new Date().toISOString()
      };
    }
  } catch (error) {
    console.error('Error sending signed document notification:', error);
    throw error;
  }
};

export default {
  validateEmail,
  sendDocumentForSigning,
  sendSignedDocumentNotification
};
